/** COMPOSITOR LEGO — arma diseños insertando elementos y herrajes del Catálogo Soluarq.
 *  Aditivo: no toca PABM/VTF ni el Diseñador 2D. diseno = {tipo:'composicion', piezas[], herrajes[], holgura_mm} */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Trash, Copy, Plus, CursorClick } from '@phosphor-icons/react';

const uid = () => Math.random().toString(36).slice(2, 9);
const SEL = 'h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1';
export const composicionDefault = () => ({ tipo: 'composicion', piezas: [], herrajes: [], holgura_mm: 0, version: 1 });

export const ComposerLego = ({ value, onChange, onAplicar }) => {
  const d = useMemo(() => ({ ...composicionDefault(), ...(value || {}) }), [value]);
  const [cat, setCat] = useState([]);
  const [q, setQ] = useState('');
  const [modo, setModo] = useState(null);      // item del catálogo en modo INSERTAR VIVO
  const [sel, setSel] = useState(null);        // {tipo:'pieza'|'herraje', id}
  const [nuevoItem, setNuevoItem] = useState(null); // form crear item inexistente
  const [precioDlg, setPrecioDlg] = useState(null); // {codigo, nombre, valor}
  const dragRef = useRef(null);

  useEffect(() => { api.get('/sa/catalogo').then((r) => setCat(r.data.items || [])).catch(() => {}); }, []);
  const catPor = (codigo) => cat.find((c) => c.codigo === codigo);
  const set = (patch) => onChange({ ...d, ...patch });

  const qn = q.trim().toLowerCase();
  const filtrados = (sec) => cat.filter((c) => (c.seccion || 'herraje') === sec && (!qn || `${c.codigo} ${c.nombre}`.toLowerCase().includes(qn))).slice(0, 30);

  // ── escala del lienzo ──
  const CW = 640, CH = 400, M = 30;
  const maxX = Math.max(2000, ...d.piezas.map((p) => p.x + p.ancho)), maxY = Math.max(2000, ...d.piezas.map((p) => p.y + p.alto));
  const esc = Math.min((CW - M * 2) / maxX, (CH - M * 2) / maxY);
  const px = (mm) => M + mm * esc;

  const snap = (p, piezas) => {
    const H = Number(d.holgura_mm || 0), T = 120;
    let { x, y } = p;
    piezas.forEach((o) => {
      if (o.pid === p.pid) return;
      if (Math.abs(x - (o.x + o.ancho + H)) < T) x = o.x + o.ancho + H;
      if (Math.abs((x + p.ancho) - (o.x - H)) < T) x = o.x - H - p.ancho;
      if (Math.abs(y - (o.y + o.alto + H)) < T) y = o.y + o.alto + H;
      if (Math.abs((y + p.alto) - (o.y - H)) < T) y = o.y - H - p.alto;
      if (Math.abs(x - o.x) < T) x = o.x;
      if (Math.abs(y - o.y) < T) y = o.y;
    });
    return { x: Math.max(0, Math.round(x)), y: Math.max(0, Math.round(y)) };
  };

  const clickCanvas = (e) => {
    if (!modo) { setSel(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const k = r.width / CW || 1;
    const mx = ((e.clientX - r.left) / k - M) / esc, my = ((e.clientY - r.top) / k - M) / esc;
    if ((modo.seccion || 'herraje') === 'elemento') {
      const p = { pid: uid(), item_codigo: modo.codigo, nombre: modo.nombre, x: mx, y: my, ancho: 1000, alto: 1000, costo_local: null };
      const pos = snap({ ...p }, d.piezas);
      set({ piezas: [...d.piezas, { ...p, ...pos }] });
      setSel({ tipo: 'pieza', id: p.pid });
      const comp = modo.composicion || [];
      if (comp.length) toast.info(`${modo.nombre} incluye: ${comp.map((c) => `${c.cantidad}× ${catPor(c.item_codigo)?.nombre || c.item_codigo}`).join(', ')}`);
    } else {
      const h = { hid: uid(), item_codigo: modo.codigo, nombre: modo.nombre, x: Math.round(mx), y: Math.round(my), costo_local: null };
      const dentro = d.piezas.find((p) => mx >= p.x && mx <= p.x + p.ancho && my >= p.y && my <= p.y + p.alto);
      if (dentro) {
        const inc = (catPor(dentro.item_codigo)?.composicion || []).find((c) => c.item_codigo === modo.codigo);
        if (inc) toast.warning(`⚠ ${dentro.nombre} YA INCLUYE ${inc.cantidad}× ${modo.nombre} en su composición — este se agrega como EXTRA`);
      }
      set({ herrajes: [...d.herrajes, h] });
      setSel({ tipo: 'herraje', id: h.hid });
    }
    if (!Number(modo.costo)) toast.error(`⚠ ${modo.nombre} no tiene precio — asígnalo antes de cerrar`);
  };

  const startDrag = (e, tipo, id) => {
    e.stopPropagation();
    setSel({ tipo, id });
    const o = tipo === 'pieza' ? d.piezas.find((p) => p.pid === id) : d.herrajes.find((h) => h.hid === id);
    dragRef.current = { tipo, id, sx: e.clientX, sy: e.clientY, x0: o.x, y0: o.y };
  };
  const onMove = (e) => {
    const g = dragRef.current; if (!g) return;
    const k = e.currentTarget.getBoundingClientRect().width / CW || 1;
    const nx = g.x0 + (e.clientX - g.sx) / (esc * k), ny = g.y0 + (e.clientY - g.sy) / (esc * k);
    if (g.tipo === 'pieza') {
      set({ piezas: d.piezas.map((p) => (p.pid === g.id ? { ...p, ...snap({ ...p, x: nx, y: ny }, d.piezas) } : p)) });
    } else set({ herrajes: d.herrajes.map((h) => (h.hid === g.id ? { ...h, x: Math.round(nx), y: Math.round(ny) } : h)) });
  };
  const endDrag = () => { dragRef.current = null; };

  const pieza = sel?.tipo === 'pieza' ? d.piezas.find((p) => p.pid === sel.id) : null;
  const herr = sel?.tipo === 'herraje' ? d.herrajes.find((h) => h.hid === sel.id) : null;
  const patchPieza = (patch) => set({ piezas: d.piezas.map((p) => (p.pid === pieza.pid ? { ...p, ...patch } : p)) });
  const patchHerr = (patch) => set({ herrajes: d.herrajes.map((h) => (h.hid === herr.hid ? { ...h, ...patch } : h)) });
  const duplicar = () => {
    if (pieza) { const n = { ...pieza, pid: uid(), x: pieza.x + pieza.ancho + Number(d.holgura_mm || 0), y: pieza.y }; set({ piezas: [...d.piezas, n] }); setSel({ tipo: 'pieza', id: n.pid }); }
    if (herr) { const n = { ...herr, hid: uid(), x: herr.x + 150 }; set({ herrajes: [...d.herrajes, n] }); setSel({ tipo: 'herraje', id: n.hid }); }
  };
  const eliminar = () => {
    if (pieza) set({ piezas: d.piezas.filter((p) => p.pid !== pieza.pid) });
    if (herr) set({ herrajes: d.herrajes.filter((h) => h.hid !== herr.hid) });
    setSel(null);
  };

  const costoDe = (obj) => obj.costo_local != null ? Number(obj.costo_local) : Number(catPor(obj.item_codigo)?.costo || 0);
  const sinPrecio = [...d.piezas, ...d.herrajes].filter((o) => !costoDe(o) && !(catPor(o.item_codigo)?.composicion || []).length);

  // ── numeración estilo Formato Solicitud: cada tipo de herraje recibe un número ──
  const numeros = {};
  d.herrajes.forEach((h) => { const k = h.item_codigo || h.nombre; if (!numeros[k]) numeros[k] = Object.keys(numeros).length + 1; });
  const leyenda = Object.entries(numeros).map(([k, n]) => ({
    n, k,
    nombre: d.herrajes.find((h) => (h.item_codigo || h.nombre) === k)?.nombre || k,
    cant: d.herrajes.filter((h) => (h.item_codigo || h.nombre) === k).length,
  }));

  const guardarPrecio = async (fijo) => {
    const { codigo, valor } = precioDlg;
    const v = Number(valor || 0);
    if (!v) return toast.error('Ingresa el precio');
    if (fijo) {
      const it = catPor(codigo);
      if (it) {
        try {
          await api.put(`/sa/catalogo/${it.id}`, { ...it, costo: v, motivo_costo: 'Asignado desde Compositor Lego' });
          toast.success(`Q${v} guardado en Catálogo Soluarq como precio fijo`);
          setCat((arr) => arr.map((c) => (c.id === it.id ? { ...c, costo: v } : c)));
        } catch (e) { return toast.error(e?.response?.data?.detail || 'No se pudo guardar en catálogo'); }
      }
    } else {
      set({
        piezas: d.piezas.map((p) => (p.item_codigo === codigo ? { ...p, costo_local: v } : p)),
        herrajes: d.herrajes.map((h) => (h.item_codigo === codigo ? { ...h, costo_local: v } : h)),
      });
      toast.success(`Q${v} aplicado SOLO a esta cotización`);
    }
    setPrecioDlg(null);
  };

  const crearItem = async (alCatalogo) => {
    const n = nuevoItem;
    if (!n.codigo || !n.nombre) return toast.error('Código y nombre requeridos');
    if (alCatalogo) {
      try {
        const { data } = await api.post('/sa/catalogo', { codigo: n.codigo.toUpperCase(), nombre: n.nombre, costo: Number(n.costo || 0), seccion: n.seccion, tipo: 'material', unidad: 'unidad', familia: '', composicion: [] });
        setCat((arr) => [...arr, data]);
        toast.success(`${data.codigo} agregado al Catálogo Soluarq`);
        setModo(data);
      } catch (e) { return toast.error(e?.response?.data?.detail || 'No se pudo crear'); }
    } else {
      const local = { codigo: n.codigo.toUpperCase(), nombre: n.nombre, costo: Number(n.costo || 0), seccion: n.seccion, composicion: [], id: `local_${uid()}` };
      setCat((arr) => [...arr, local]);
      setModo(local);
      toast.info(`${local.codigo} creado SOLO para esta cotización (no está en el catálogo)`);
    }
    setNuevoItem(null);
  };

  const aplicar = () => {
    const lineas = {};
    const add = (codigo, nombre, cant, costo, unidad = 'unidad') => {
      const k = codigo || nombre;
      lineas[k] = lineas[k] || { item_codigo: codigo, descripcion: nombre, cantidad: 0, unidad, costo_unitario: costo, tipo: 'material', origen: 'manual' };
      lineas[k].cantidad += cant;
    };
    d.piezas.forEach((p) => {
      add(p.item_codigo, `${p.nombre} ${(p.ancho / 1000).toFixed(2)}×${(p.alto / 1000).toFixed(2)}m`, 1, costoDe(p), catPor(p.item_codigo)?.unidad || 'unidad');
      (catPor(p.item_codigo)?.composicion || []).forEach((c) => {
        const it = catPor(c.item_codigo);
        add(c.item_codigo, it?.nombre || c.item_codigo, Number(c.cantidad || 1), Number(it?.costo || 0), it?.unidad || 'unidad');
      });
    });
    d.herrajes.forEach((h) => add(h.item_codigo, h.nombre, 1, costoDe(h), catPor(h.item_codigo)?.unidad || 'unidad'));
    onAplicar(Object.values(lineas).map((l) => ({ ...l, cantidad: Math.round(l.cantidad * 100) / 100, costo_unitario: Number(l.costo_unitario || 0) })));
    toast.success('Materiales del diseño aplicados al elemento (paso Materiales y cantidades)');
  };

  const medida = (p, k, def) => p[k] != null && p[k] !== '' ? p[k] : def;

  return (
    <div className="border border-[var(--border)] rounded-lg p-2.5 space-y-2" data-testid="composer-lego">
      <div className="flex flex-wrap gap-1.5 items-center">
        <Input placeholder="Buscar en Catálogo Soluarq…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 text-xs w-52" data-testid="lego-buscar" />
        <label className="text-[10px] font-bold text-[var(--text-muted)]">Holgura entre piezas (mm)</label>
        <Input type="number" value={d.holgura_mm} onChange={(e) => set({ holgura_mm: Number(e.target.value || 0) })} className="h-8 w-16 text-xs" data-testid="lego-holgura" />
        <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => setNuevoItem({ codigo: '', nombre: '', costo: '', seccion: 'herraje' })} data-testid="lego-nuevo-item"><Plus size={13} className="mr-1" />No existe — crear</Button>
        {modo && (
          <span className="text-[10px] font-black text-emerald-600 flex items-center gap-1 border border-emerald-500/50 rounded-full px-2 py-0.5" data-testid="lego-modo-vivo">
            <CursorClick size={12} /> INSERTANDO: {modo.nombre} — clic en el lienzo las veces necesarias
            <button type="button" className="ml-1 font-black text-red-500" onClick={() => setModo(null)} data-testid="lego-modo-off">✕ terminar</button>
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {['elemento', 'herraje'].map((sec) => (
          <div key={sec} className="border border-dashed border-[var(--border)] rounded-md p-1.5 max-h-28 overflow-y-auto">
            <p className="text-[9px] font-black uppercase text-[#0EA5E9] mb-1">{sec === 'elemento' ? 'Elementos (ventanas, puertas, vidrios, pasamanos, refuerzos…)' : 'Herrajes / materiales'}</p>
            <div className="flex flex-wrap gap-1">
              {filtrados(sec).map((c) => (
                <button key={c.id || c.codigo} type="button" onClick={() => { setModo(c); setSel(null); }} data-testid={`lego-pick-${c.codigo}`}
                  className={`text-[10px] px-1.5 py-0.5 rounded border ${modo?.codigo === c.codigo ? 'border-emerald-500 bg-emerald-500/10 font-black' : 'border-[var(--border)] hover:border-[#0EA5E9]'} ${!Number(c.costo) ? 'text-red-500' : 'text-[var(--text)]'}`}
                  title={`${c.codigo} · Q${Number(c.costo || 0).toFixed(2)}${!Number(c.costo) ? ' — SIN PRECIO' : ''}`}>
                  {c.nombre}{!Number(c.costo) && ' ⚠'}
                </button>
              ))}
              {filtrados(sec).length === 0 && <p className="text-[10px] text-[var(--text-muted)] italic">Sin resultados{sec === 'elemento' ? ' — marca items como Elemento en el Catálogo Soluarq' : ''}.</p>}
            </div>
          </div>
        ))}
      </div>

      {nuevoItem && (
        <div className="flex flex-wrap gap-1.5 items-center border border-dashed border-amber-500 rounded-md p-2" data-testid="lego-nuevo-form">
          <Input placeholder="Código" value={nuevoItem.codigo} onChange={(e) => setNuevoItem({ ...nuevoItem, codigo: e.target.value.toUpperCase() })} className="h-8 w-24 text-xs" data-testid="lego-nuevo-codigo" />
          <Input placeholder="Nombre" value={nuevoItem.nombre} onChange={(e) => setNuevoItem({ ...nuevoItem, nombre: e.target.value })} className="h-8 flex-1 text-xs" data-testid="lego-nuevo-nombre" />
          <Input placeholder="Precio Q" type="number" value={nuevoItem.costo} onChange={(e) => setNuevoItem({ ...nuevoItem, costo: e.target.value })} className="h-8 w-20 text-xs" />
          <select value={nuevoItem.seccion} onChange={(e) => setNuevoItem({ ...nuevoItem, seccion: e.target.value })} className={SEL}>
            <option value="herraje">Herraje</option><option value="elemento">Elemento</option>
          </select>
          <Button type="button" size="sm" className="h-8 text-xs bg-[#0EA5E9] text-white" onClick={() => crearItem(true)} data-testid="lego-nuevo-a-catalogo">Agregar al catálogo</Button>
          <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => crearItem(false)} data-testid="lego-nuevo-local">Solo esta cotización</Button>
          <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setNuevoItem(null)}>Cancelar</Button>
        </div>
      )}

      <div className="bg-[var(--bg)] rounded-lg flex justify-center">
        <svg viewBox={`0 0 ${CW} ${CH}`} className="w-full max-w-[640px] h-auto" onClick={clickCanvas} onPointerMove={onMove} onPointerUp={endDrag} onPointerLeave={endDrag}
          style={{ cursor: modo ? 'crosshair' : 'default' }} data-testid="lego-canvas">
          {d.piezas.map((p) => {
            const on = sel?.tipo === 'pieza' && sel.id === p.pid;
            const pend = !costoDe(p);
            return (
              <g key={p.pid} onPointerDown={(e) => startDrag(e, 'pieza', p.pid)} onClick={(e) => { if (!modo) e.stopPropagation(); }} style={{ cursor: 'grab' }} data-testid={`lego-pieza-${p.pid}`}>
                <rect x={px(p.x)} y={px(p.y)} width={p.ancho * esc} height={p.alto * esc}
                  fill={on ? 'rgba(14,165,233,0.15)' : 'rgba(148,163,184,0.08)'} stroke={pend ? '#DC2626' : on ? '#0EA5E9' : 'var(--text-muted)'} strokeWidth={on ? 2.5 : 1.5} />
                <text x={px(p.x) + p.ancho * esc / 2} y={px(p.y) + p.alto * esc / 2} textAnchor="middle" fontSize="9" fontWeight="700" fill={on ? '#0EA5E9' : 'var(--text-muted)'}>{p.nombre.slice(0, 22)}</text>
                <text x={px(p.x) + p.ancho * esc / 2} y={px(p.y) + p.alto * esc / 2 + 11} textAnchor="middle" fontSize="8" fill="var(--text-muted)">
                  {p.ancho_arriba != null || p.ancho_abajo != null
                    ? `↑${medida(p, 'ancho_arriba', p.ancho)} ↓${medida(p, 'ancho_abajo', p.ancho)} × ${Math.round(p.alto)} mm`
                    : `${Math.round(p.ancho)} × ${Math.round(p.alto)} mm`}{pend ? ' · Q?' : ''}
                </text>
              </g>
            );
          })}
          {d.herrajes.map((h) => {
            const on = sel?.tipo === 'herraje' && sel.id === h.hid;
            const num = numeros[h.item_codigo || h.nombre];
            return (
              <g key={h.hid} onPointerDown={(e) => startDrag(e, 'herraje', h.hid)} onClick={(e) => { if (!modo) e.stopPropagation(); }} style={{ cursor: 'grab' }} data-testid={`lego-herraje-${h.hid}`}>
                <circle cx={px(h.x)} cy={px(h.y)} r={8} fill={on ? '#0EA5E9' : costoDe(h) ? '#B45309' : '#DC2626'} />
                <text x={px(h.x)} y={px(h.y) + 3} textAnchor="middle" fontSize="9" fontWeight="900" fill="#fff">{num}</text>
              </g>
            );
          })}
          {d.piezas.length === 0 && <text x={CW / 2} y={CH / 2} textAnchor="middle" fontSize="12" fill="var(--text-muted)">Elige un elemento del catálogo y haz clic aquí para insertarlo (tipo Lego)</text>}
        </svg>
      </div>

      {leyenda.length > 0 && (
        <div className="border border-amber-500/40 rounded-md px-2 py-1.5 flex flex-wrap gap-x-3 gap-y-1" data-testid="lego-leyenda">
          <span className="text-[10px] font-black uppercase text-amber-600">Leyenda de herrajes:</span>
          {leyenda.map((l) => (
            <span key={l.k} className="text-[10px] text-[var(--text)]" data-testid={`lego-leyenda-${l.n}`}>
              <b className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#B45309] text-white text-[8px] font-black mr-1">{l.n}</b>
              {l.nombre} ×{l.cant}
            </span>
          ))}
        </div>
      )}

      {sinPrecio.length > 0 && (
        <p className="text-[11px] font-black text-red-500" data-testid="lego-alerta-precio">
          ⚠ {sinPrecio.length} pieza(s)/herraje(s) SIN PRECIO: {[...new Set(sinPrecio.map((o) => o.nombre))].join(', ')} — selecciónalos y asigna precio.
        </p>
      )}

      {pieza && (
        <div className="flex flex-wrap gap-1.5 items-end border-t border-[#0EA5E9]/40 pt-2" data-testid="lego-pieza-panel">
          <span className="text-xs font-black text-[#0EA5E9] w-full sm:w-auto">{pieza.nombre} · {costoDe(pieza) ? `Q${costoDe(pieza).toFixed(2)}` : '⚠ SIN PRECIO'}</span>
          {[['ancho', 'Ancho'], ['alto', 'Alto'], ['ancho_arriba', 'Ancho arriba'], ['ancho_medio', 'Ancho medio'], ['ancho_abajo', 'Ancho abajo']].map(([k, l]) => (
            <div key={k}><label className="text-[9px] font-bold text-[var(--text-muted)]">{l} (mm)</label>
              <Input type="number" value={pieza[k] ?? ''} placeholder={k.startsWith('ancho_') ? String(pieza.ancho) : ''} onChange={(e) => patchPieza({ [k]: e.target.value === '' ? null : Number(e.target.value) })} className="h-8 w-20 text-xs" data-testid={`lego-pieza-${k}`} /></div>
          ))}
          <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={duplicar} data-testid="lego-duplicar"><Copy size={13} className="mr-1" />Duplicar</Button>
          <Button type="button" size="sm" variant="outline" className="h-8 text-xs text-red-500 border-red-500/50" onClick={eliminar} data-testid="lego-eliminar"><Trash size={13} className="mr-1" />Eliminar</Button>
          {!costoDe(pieza) && <Button type="button" size="sm" className="h-8 text-xs bg-red-600 text-white" onClick={() => setPrecioDlg({ codigo: pieza.item_codigo, nombre: pieza.nombre, valor: '' })} data-testid="lego-asignar-precio">Asignar precio</Button>}
          {(catPor(pieza.item_codigo)?.composicion || []).length > 0 && (
            <div className="w-full text-[10px] text-[var(--text-muted)]" data-testid="lego-pieza-composicion">
              <b>Incluye (del catálogo):</b> {(catPor(pieza.item_codigo).composicion).map((c) => `${c.cantidad}× ${catPor(c.item_codigo)?.nombre || c.item_codigo}`).join(' · ')}
            </div>
          )}
        </div>
      )}
      {herr && (
        <div className="flex flex-wrap gap-1.5 items-end border-t border-amber-500/40 pt-2" data-testid="lego-herraje-panel">
          <span className="text-xs font-black text-amber-600">{herr.nombre} · {costoDe(herr) ? `Q${costoDe(herr).toFixed(2)}` : '⚠ SIN PRECIO'}</span>
          <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={duplicar} data-testid="lego-duplicar-h"><Copy size={13} className="mr-1" />Duplicar</Button>
          <Button type="button" size="sm" variant="outline" className="h-8 text-xs text-red-500 border-red-500/50" onClick={eliminar} data-testid="lego-eliminar-h"><Trash size={13} className="mr-1" />Eliminar</Button>
          {!costoDe(herr) && <Button type="button" size="sm" className="h-8 text-xs bg-red-600 text-white" onClick={() => setPrecioDlg({ codigo: herr.item_codigo, nombre: herr.nombre, valor: '' })} data-testid="lego-asignar-precio-h">Asignar precio</Button>}
        </div>
      )}

      {precioDlg && (
        <div className="flex flex-wrap gap-1.5 items-center border border-red-500/60 rounded-md p-2" data-testid="lego-precio-dlg">
          <span className="text-[11px] font-bold">Precio de {precioDlg.nombre}: Q</span>
          <Input autoFocus type="number" value={precioDlg.valor} onChange={(e) => setPrecioDlg({ ...precioDlg, valor: e.target.value })} className="h-8 w-24 text-xs" data-testid="lego-precio-input" />
          <Button type="button" size="sm" className="h-8 text-xs bg-[#0EA5E9] text-white" onClick={() => guardarPrecio(true)} data-testid="lego-precio-fijo">Fijo — guardar en catálogo</Button>
          <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => guardarPrecio(false)} data-testid="lego-precio-solo">Solo esta cotización</Button>
          <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setPrecioDlg(null)}>Cancelar</Button>
        </div>
      )}

      <div className="flex justify-between items-center">
        <p className="text-[10px] text-[var(--text-muted)]">{d.piezas.length} elemento(s) · {d.herrajes.length} herraje(s) · Total materiales: Q{([...d.piezas.map((p) => costoDe(p) + (catPor(p.item_codigo)?.composicion || []).reduce((s, c) => s + Number(c.cantidad || 1) * Number(catPor(c.item_codigo)?.costo || 0), 0)), ...d.herrajes.map(costoDe)].reduce((a, b) => a + b, 0)).toFixed(2)}</p>
        <Button type="button" size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={aplicar} data-testid="lego-aplicar">Aplicar materiales al elemento</Button>
      </div>
    </div>
  );
};
